import {
  Modal,
  type ModalProps,
  type SelectProps,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getKey, getValue } from './helpers';
import { SelectItemModal } from './SelectItemModal';
import type { Value } from './types';

export type SelectModalProps = ModalProps &
  Pick<
    SelectProps,
    'data' | 'clearable' | 'searchable' | 'onSearchChange' | 'searchValue'
  > & {
    label?: React.ReactNode;
    value: Value;
    onChange: (value: Value) => void;
  };

/**
 * SelectModal component
 * A modal that displays a list of selectable items.
 *
 * @param data - Array of items to display in the modal.
 * @param label - Optional label for the modal.
 * @param value - The currently selected value.
 * @param onChange - Callback function to handle value changes.
 * @param searchable - Whether the modal should have a search input.
 * @param clearable - Whether the modal should allow clearing the selection.
 * @param modalProps - Additional properties for the modal component.
 * @param onSearchChange - Callback function to handle search input changes.
 * @param searchValue - The current value of the search input.
 */
export const SelectModal: React.FC<SelectModalProps> = ({
  data,
  label,
  value,
  onChange,
  searchable,
  clearable,
  onSearchChange,
  searchValue = '',
  ...modalProps
}) => {
  const { t } = useTranslation(['common']);
  const [search, setSearch] = useState(searchValue);

  /**
   * Updates the search input value and triggers the onSearchChange callback.
   * @param value
   */
  const updateSearch = useCallback(
    (value: string) => {
      setSearch(value);
      onSearchChange?.(value);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setSearch],
  );

  /**
   * Effect to reset the search input when the modal is closed.
   */
  useEffect(() => {
    if (!modalProps.opened) {
      updateSearch('');
    }
  }, [modalProps.opened, updateSearch]);

  /**
   * Filters the items based on the search input.
   */
  const localFilter = data.filter(item => {
    const itemValue = getValue(item);
    const itemLabel = getKey(item);
    const toCheck = typeof itemValue === 'string' ? itemValue : itemLabel;
    return toCheck.toLowerCase().includes(search.toLowerCase());
  });

  /**
   * Filters the items based on the search input.
   * When onSearchChange is provided, it uses the data from the parent component.
   */
  const items = onSearchChange ? data : localFilter;

  return (
    <Modal
      title={label && typeof label === 'string' ? t(label) : label}
      size="lg"
      overflow="outside"
      centered
      {...modalProps}
    >
      {searchable && (
        <TextInput
          placeholder={t('common:search')}
          onChange={event => updateSearch(event.target.value)}
        />
      )}
      <Stack spacing={0}>
        {clearable && search === '' && value !== null && (
          <SelectItemModal item={null} onClick={() => onChange(null)}>
            <Text fz="md" italic>
              {t('common:clearVariable')}
            </Text>
          </SelectItemModal>
        )}
        {items.map(item => (
          <SelectItemModal
            isSelected={getValue(item) === value}
            onClick={() => onChange(getValue(item))}
            key={getKey(item)}
            item={item}
          />
        ))}
        {items.length === 0 && (
          <Text fz="md" color="dimmed" p={16} align="center">
            {t('common:notFoundResults')}
          </Text>
        )}
      </Stack>
    </Modal>
  );
};
